import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import "./Dashboard.css";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
import { useSettings } from "../../context/SettingsContext";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { useDashboardLayout } from "../../context/LayoutContext";
import useDashboardMetrics from "../../hooks/useDashboardMetrics";
import PageLayoutRenderer from "../uiLayout/PageLayoutRenderer";
import FleetDashboardLegacyView from "./FleetDashboardLegacyView";

export default function DashboardDatas() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state: userState, organizationInactive } = useUser();
  const { fetchSettings, settings, loading: settingsLoading } = useSettings();
  const organizationId = userState?.user?.organizationId;
  const useJsonLayout = isJsonDrivenLayoutEnabled();

  const { layout } = useDashboardLayout(useJsonLayout ? PAGE_IDS.FLEET_DASHBOARD : null);

  useEffect(() => {
    if (!organizationId) return;
    fetchSettings(organizationId).catch((error) => {
      console.error("Error fetching dashboard settings:", error);
    });
  }, [organizationId, fetchSettings]);

  const { metrics, errorsBySource } = useDashboardMetrics(
    useJsonLayout ? layout : null,
    { organizationId },
  );

  useEffect(() => {
    const hasUnauthorizedSource = Object.values(errorsBySource).some(
      (error) => error?.response?.status === 401,
    );
    if (hasUnauthorizedSource) {
      navigate("/");
    }
  }, [errorsBySource, navigate]);

  const renderCardSkeletons = () =>
    Array(6)
      .fill(0)
      .map((_, index) => (
        <div key={`skeleton-card-${index}`} className="dashboard-card">
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="70%" height={30} />
          <Skeleton variant="rectangular" width="100%" height={120} sx={{ mt: 2 }} />
        </div>
      ));

  const renderUpcomingTripSkeleton = () => (
    <div className="dashboard-upcoming-section">
      <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={200} />
    </div>
  );

  const renderNoPermissionsState = () => (
    <div className="no-permissions-state">
      <p>{t("noDataFound")}</p>
    </div>
  );

  const isLoading = useJsonLayout ? false : settingsLoading;

  if (!useJsonLayout) {
    return (
      <div className="dashboard-container">
        {isLoading ? (
          <div className="row">
            <div
              className={`dashboard-grid-container${organizationInactive ? " dashboard-grid-container--full" : ""}`}
            >
              {renderCardSkeletons()}
            </div>
            {!organizationInactive && renderUpcomingTripSkeleton()}
          </div>
        ) : !settings ? (
          <div className="error-state">
            <p>{t("failed_to_load_settings")}</p>
          </div>
        ) : (
          <FleetDashboardLegacyView
            organizationId={organizationId}
            userFeatures={userState?.features}
            onUnauthorized={() => navigate("/")}
          />
        )}
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {isLoading ? (
        <div className="row">
          <div
            className={`dashboard-grid-container${organizationInactive ? " dashboard-grid-container--full" : ""}`}
          >
            {renderCardSkeletons()}
          </div>
          {!organizationInactive && renderUpcomingTripSkeleton()}
        </div>
      ) : layout ? (
        <PageLayoutRenderer
          layout={layout}
          metrics={metrics}
          t={t}
          userFeatures={userState?.features}
        />
      ) : (
        renderNoPermissionsState()
      )}
    </div>
  );
}
